import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { C, Btn, Input, Card } from "../components/UI";

// Mesmo layout usado nas telas de login e cadastro
function AuthLayout({ children, title, sub }) {
  return (
    <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 60%)`, padding: 24 }}>
      <Card style={{ width: "100%", maxWidth: 440, padding: "40px 36px" }} className="anim-1">
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: C.roseMid, marginBottom: 6 }}>WoMom</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{title}</h1>
          <p style={{ fontSize: 14, color: C.textSec }}>{sub}</p>
        </div>
        {children}
      </Card>
    </div>
  );
}

// ── Pedir o e-mail de recuperação ──────────────────────────────────────────────
export function EsqueciSenha() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [enviado, setEnviado] = useState(false);
  const navigate = useNavigate();

  const enviar = async () => {
    if (!email.trim()) {
      setErro("Informe o e-mail da sua conta.");
      return;
    }
    setLoading(true);
    setErro("");

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/nova-senha`,
    });

    setLoading(false);

    if (error) {
      console.error("Erro ao pedir recuperação de senha:", error);
      if (error.message?.includes("security purposes") || error.message?.includes("rate limit")) {
        setErro("Aguarde alguns segundos antes de tentar de novo.");
      } else {
        setErro("Não foi possível enviar o e-mail agora. Tente novamente em instantes.");
      }
      return;
    }

    // Mostramos a mesma mensagem exista ou não a conta.
    // Dizer "esse e-mail não está cadastrado" revelaria quem tem conta na WoMom.
    setEnviado(true);
  };

  if (enviado) {
    return (
      <AuthLayout title="Verifique seu e-mail" sub="Se existe uma conta com esse endereço">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 15, color: C.text, marginBottom: 8, fontWeight: 500 }}>
            Enviamos um link para:
          </p>
          <p style={{ fontSize: 15, color: C.roseMid, marginBottom: 20, fontWeight: 600 }}>
            {email.trim()}
          </p>
          <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24, lineHeight: 1.6 }}>
            Clique no link para escolher uma senha nova. Ele vale por uma hora.
          </p>
          <div style={{ background: C.grayBg, borderRadius: 10, padding: "14px 16px", fontSize: 13, color: C.textSec, marginBottom: 24, lineHeight: 1.5 }}>
            Não recebeu? Verifique a caixa de spam. Se o e-mail não estiver cadastrado, nenhuma mensagem é enviada.
          </div>
          <Btn variant="ghost" onClick={() => navigate("/login")}>Voltar para o login</Btn>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Esqueci minha senha" sub="Enviamos um link para você criar uma nova">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Input
          label="E-mail"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="seu@email.com"
          required
        />
      </div>

      {erro && <p style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{erro}</p>}

      <Btn full onClick={enviar} disabled={loading}>
        {loading ? "Enviando..." : "Enviar link de recuperação"}
      </Btn>

      <p style={{ textAlign: "center", fontSize: 13, color: C.textSec, marginTop: 20 }}>
        Lembrou a senha?{" "}
        <span onClick={() => navigate("/login")} style={{ color: C.roseMid, cursor: "pointer", fontWeight: 500 }}>Entrar</span>
      </p>
    </AuthLayout>
  );
}

// ── Definir a senha nova ───────────────────────────────────────────────────────
export function NovaSenha() {
  const [senha, setSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [verificando, setVerificando] = useState(true);
  const [linkValido, setLinkValido] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);
  const navigate = useNavigate();

  // O Supabase lê o token que veio na URL e cria uma sessão temporária.
  // Isso é assíncrono, então esperamos: ou o evento de autenticação chega,
  // ou depois de alguns segundos consideramos o link inválido.
  useEffect(() => {
    let ativo = true;

    const { data: listener } = supabase.auth.onAuthStateChange((_evento, session) => {
      if (ativo && session) {
        setLinkValido(true);
        setVerificando(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!ativo) return;
      if (data?.session) {
        setLinkValido(true);
        setVerificando(false);
      }
    });

    const limite = setTimeout(() => {
      if (ativo) setVerificando(false);
    }, 2500);

    return () => {
      ativo = false;
      clearTimeout(limite);
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const salvar = async () => {
    if (senha.length < 6) {
      setErro("A senha deve ter no mínimo 6 caracteres.");
      return;
    }
    if (senha !== confirma) {
      setErro("As senhas não são iguais.");
      return;
    }
    setLoading(true);
    setErro("");

    const { error } = await supabase.auth.updateUser({ password: senha });

    setLoading(false);

    if (error) {
      console.error("Erro ao trocar a senha:", error);
      setErro("Não foi possível trocar a senha. O link pode ter expirado — peça um novo.");
      return;
    }

    // Encerra a sessão temporária do link, para a pessoa entrar com a senha nova
    await supabase.auth.signOut();
    setSucesso(true);
  };

  if (verificando) {
    return (
      <AuthLayout title="Um instante" sub="Verificando o link">
        <p style={{ textAlign: "center", fontSize: 14, color: C.textSec }}>Carregando...</p>
      </AuthLayout>
    );
  }

  if (sucesso) {
    return (
      <AuthLayout title="Senha alterada" sub="Pronto, pode entrar de novo">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24, lineHeight: 1.6 }}>
            Sua senha foi trocada. Use a nova para entrar na sua conta.
          </p>
          <Btn full onClick={() => navigate("/login")}>Ir para o login</Btn>
        </div>
      </AuthLayout>
    );
  }

  if (!linkValido) {
    return (
      <AuthLayout title="Link inválido ou expirado" sub="Peça um novo para continuar">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24, lineHeight: 1.6 }}>
            Links de recuperação valem por uma hora e só podem ser usados uma vez.
          </p>
          <Btn full onClick={() => navigate("/esqueci-senha")}>Pedir um link novo</Btn>
          <Btn variant="ghost" full onClick={() => navigate("/login")} style={{ marginTop: 8, fontSize: 13 }}>
            Voltar para o login
          </Btn>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Criar senha nova" sub="Escolha uma senha para a sua conta">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Input
          label="Nova senha"
          type="password"
          value={senha}
          onChange={e => setSenha(e.target.value)}
          placeholder="Mínimo 6 caracteres"
          required
        />
        <Input
          label="Confirmar nova senha"
          type="password"
          value={confirma}
          onChange={e => setConfirma(e.target.value)}
          placeholder="Digite a senha novamente"
          required
        />
      </div>

      {erro && <p style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{erro}</p>}

      <Btn full onClick={salvar} disabled={loading}>
        {loading ? "Salvando..." : "Salvar nova senha"}
      </Btn>
    </AuthLayout>
  );
}
